import { Link } from '@tanstack/react-router';
import { formatInTimeZone } from 'date-fns-tz';
import { type ReactNode } from 'react';

import {
    OUTREACH_ANALYTICS_TIMEZONE,
    type OutreachUrgencyFilter,
} from '@apps/web/components/admin/outreach_observability/constants';
import { Badge } from '@apps/web/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { useAdminOutreachJudgeRuns } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type JudgeRunListItem } from '@libraries/typescript/api_client';

function formatCreatedAt(iso: string): string {
    return formatInTimeZone(
        new Date(iso),
        OUTREACH_ANALYTICS_TIMEZONE,
        'M/d h:mm a',
    );
}

function JudgeRunsBody({
    runs,
    totalMatching,
    truncated,
    isLoading,
    isError,
}: {
    readonly runs: Array<JudgeRunListItem>;
    readonly totalMatching: number;
    readonly truncated: boolean;
    readonly isLoading: boolean;
    readonly isError: boolean;
}) {
    if (runs.length === 0) {
        if (isLoading) {
            return (
                <div className="text-muted-foreground py-6 text-center text-sm">
                    Loading runs…
                </div>
            );
        }
        if (isError) {
            return (
                <div className="text-destructive py-6 text-center text-sm">
                    Failed to load runs.
                </div>
            );
        }
        return (
            <div className="text-muted-foreground py-6 text-center text-sm">
                No judged runs in this window.
            </div>
        );
    }
    return (
        <div className="flex flex-col gap-2">
            <div className="text-muted-foreground text-xs">
                {truncated
                    ? `Showing ${runs.length} of ${totalMatching} runs (newest first)`
                    : `${totalMatching} runs`}
            </div>
            <div className="max-h-80 overflow-auto rounded-md border">
                <table className="w-full text-xs">
                    <thead className="bg-muted/50 sticky top-0">
                        <tr className="text-muted-foreground text-left">
                            <th className="px-3 py-2 font-medium">Time</th>
                            <th className="px-3 py-2 font-medium">Org</th>
                            <th className="px-3 py-2 font-medium">Grade</th>
                            <th className="px-3 py-2 font-medium">Flags</th>
                            <th className="px-3 py-2 font-medium">Summary</th>
                            <th className="px-3 py-2 font-medium">Links</th>
                        </tr>
                    </thead>
                    <tbody>
                        {runs.map((run) => (
                            <tr
                                // One row per judge note: a conversation judged
                                // twice in the window appears twice, so the
                                // timestamp disambiguates the key.
                                key={`${run.agent_conversation_id}:${run.created_at}`}
                                className="border-t align-top"
                            >
                                <td className="px-3 py-2 whitespace-nowrap tabular-nums">
                                    {formatCreatedAt(run.created_at)}
                                </td>
                                <td className="px-3 py-2">
                                    {run.org_name ?? '—'}
                                </td>
                                <td className="px-3 py-2">
                                    {run.quality_grade ? (
                                        <Badge variant="secondary">
                                            {run.quality_grade}
                                        </Badge>
                                    ) : (
                                        '—'
                                    )}
                                </td>
                                <td className="px-3 py-2">
                                    {run.labels.length > 0 ? (
                                        <div className="flex flex-wrap gap-1">
                                            {run.labels.map((label) => (
                                                <Badge
                                                    key={label}
                                                    variant="destructive"
                                                >
                                                    {label}
                                                </Badge>
                                            ))}
                                        </div>
                                    ) : (
                                        '—'
                                    )}
                                </td>
                                <td className="text-muted-foreground max-w-xs truncate px-3 py-2">
                                    {run.summary}
                                </td>
                                <td className="px-3 py-2">
                                    <Link
                                        to="/admin/agent-traces/$conversationId"
                                        params={{
                                            conversationId:
                                                run.agent_conversation_id,
                                        }}
                                        className="text-primary hover:underline"
                                    >
                                        Trace
                                    </Link>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// Shared judge-runs drill-down modal: fetches judged / flagged outreach runs
// (only while open) and renders them as a table. `footer` renders an optional
// action row (the hero tile passes a "Go to Quality" navigation link).
export function JudgeRunsDialog({
    open,
    onOpenChange,
    title,
    description,
    organizationIds,
    urgency,
    grade,
    flaggedOnly,
    day,
    footer,
}: {
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
    readonly title: string;
    readonly description?: string;
    readonly organizationIds: Array<string>;
    readonly urgency: OutreachUrgencyFilter;
    readonly grade?: string;
    readonly flaggedOnly?: boolean;
    readonly day?: string;
    readonly footer?: ReactNode;
}) {
    const runsQuery = useAdminOutreachJudgeRuns(organizationIds, open, {
        grade,
        flaggedOnly,
        day,
        urgency,
    });
    const data = runsQuery.data;
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    {description ? (
                        <DialogDescription>{description}</DialogDescription>
                    ) : null}
                </DialogHeader>
                <JudgeRunsBody
                    runs={data?.runs ?? []}
                    totalMatching={data?.total_matching ?? 0}
                    truncated={data?.truncated ?? false}
                    isLoading={runsQuery.isLoading}
                    isError={
                        runsQuery.error !== undefined ||
                        (runsQuery.hasRequestFailed && data === undefined)
                    }
                />
                {footer ? <DialogFooter>{footer}</DialogFooter> : null}
            </DialogContent>
        </Dialog>
    );
}
